import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useTexture, Text } from '@react-three/drei';
import * as THREE from 'three';
import audioManager from '../../utils/audioManager';

const ShatterWall = ({
    word,
    image,
    position = [0, 0, -20],
    currentZ,
    onReachPlayer,
    status, // Changed from result to status
    onShatterComplete,
    isGameActive
}) => {
    const wallRef = useRef();
    const fragmentsRef = useRef([]);
    const [shattered, setShattered] = useState(false);

    // Load textures
    const wallTexture = useTexture(process.env.PUBLIC_URL + '/assets/gameplay/wall.png');
    const wordTexture = useTexture(process.env.PUBLIC_URL + image);

    // Create debris pieces if shattered
    const fragments = useMemo(() => {
        const pieces = [];
        const count = 16;
        for (let i = 0; i < count; i++) {
            pieces.push({
                position: [
                    (Math.random() - 0.5) * 4,
                    (Math.random() - 0.5) * 4,
                    (Math.random() - 0.5) * 1
                ],
                velocity: [
                    (Math.random() - 0.5) * 10,
                    (Math.random() - 0.5) * 10 + 5,
                    (Math.random() - 0.5) * 10 - 10
                ],
                rotation: [Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI],
                scale: Math.random() * 0.5 + 0.2
            });
        }
        return pieces;
    }, []);

    useEffect(() => {
        if (status === 'cleared' && !shattered) {
            setShattered(true);
            audioManager.playSFX('sfxWallBreak');
            setTimeout(onShatterComplete, 600);
        }
    }, [status, shattered, onShatterComplete]);

    useFrame((state, delta) => {
        if (!isGameActive) return;

        if (!shattered) {
            if (wallRef.current) {
                // Positon is set by parent via currentZ
                wallRef.current.position.z = currentZ;
            }
        } else {
            // Animate fragments
            fragmentsRef.current.forEach((fragment, i) => {
                if (fragment) {
                    fragment.position.x += fragments[i].velocity[0] * delta;
                    fragment.position.y += fragments[i].velocity[1] * delta;
                    fragment.position.z += fragments[i].velocity[2] * delta;
                    fragment.rotation.x += 1 * delta;
                    fragment.rotation.y += 1 * delta;
                    fragments[i].velocity[1] -= 20 * delta; // Gravity
                }
            });
        }
    });

    if (shattered) {
        return (
            <group position={[wallRef.current?.position.x || 0, wallRef.current?.position.y || 0, currentZ]}>
                {fragments.map((frag, i) => (
                    <mesh
                        key={i}
                        ref={el => (fragmentsRef.current[i] = el)}
                        position={frag.position}
                        rotation={frag.rotation}
                    >
                        <boxGeometry args={[frag.scale, frag.scale, frag.scale]} />
                        <meshStandardMaterial map={wallTexture} />
                    </mesh>
                ))}
            </group>
        );
    }

    return (
        <group ref={wallRef} position={position}>
            {/* The main wall structure */}
            <mesh>
                <boxGeometry args={[6, 6, 0.5]} />
                <meshStandardMaterial map={wallTexture} />
            </mesh>

            {/* The word image card placed on top of the wall */}
            <mesh position={[0, 0, 0.26]}>
                <planeGeometry args={[4, 4]} />
                <meshStandardMaterial map={wordTexture} transparent />
            </mesh>

            {/* The word text above the image on the wall */}
            <Text
                position={[0, 2.3, 0.3]}
                fontSize={0.6}
                color="white"
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.05}
                outlineColor="black"
            >
                {word}
            </Text>
        </group>
    );
};

export default ShatterWall;
